import React, { useEffect } from 'react'
import { useContext } from 'react'
import { createContext } from 'react'
import { routes } from '../containers/routes'
import {
  setUser as setUserStore,
  logout as logoutStore,
  logout_no_redirect as logoutNoRedirectStore,
} from '../store/actions'
import { authController } from '../apiService'
import { HeaderBar } from '../components/HeaderBar'
import SideBar from '../containers/RouteContainer/Sidebar'
import { MainLayoutContainer } from '../containers/MainLayoutContainer'
import { localStorageService, toastFail } from '../helpers'
import UploadProgress from '../components/ProgressBar/UploadProgress'
import { Toast } from '../components'
import { useAuthUtil } from '../hooks/useAuthUtil'
import { useSelector } from 'react-redux'
import { store } from '../store'

const initUserState = {
  id: 0,
  email: '',
  full_name: '',
  roles: [],
  access_token: '',
  refresh_token: '',
}

const AuthContext = createContext({
  user: initUserState,
  role: '',
  getUser: () => {},
  login: ({ email = '', password = '' }) => {},
  logout: () => {},
})

const firstLocation = location.pathname

const AuthProvider = ({ history }) => {
  const user = useSelector(state => state.user)
  const role = useSelector(state => state.role)
  const authLogin = useAuthUtil(history)
  const setUser = newUser => store.dispatch(setUserStore(newUser))

  const getUser = async () => {
    if (user.access_token) {
      const res = await authController().getUser()

      if (res && res.id) {
        if (res.role instanceof Object) {
          res.roles = [res.role]
          res.role = res.role.id
        }
        const newUser = { ...user, ...res }
        localStorageService.setUser(newUser)
        setUser(newUser)
      } else {
        logout()
      }
    }
  }

  const login = async ({ email, password }) => {
    const res = await authController()
      .login({ email, password })
      .catch(e => e.response.data.message)

    if (res && typeof res !== 'string' && res.id) {
      if (res.role instanceof Object) {
        res.roles = [res.role]
        res.role = res.role.id
      }
      const newUser = { ...user, ...res }
      localStorageService.setUser(newUser)
      setUser(newUser)
      authLogin.loginFlow(newUser)
    } else {
      toastFail(res)
    }
  }

  const logout = (from = '') => {
    if (from) {
      console.log('Logout from ', from)
    }
    store.dispatch(logoutStore())
  }

  const storageListener = ev => {
    // console.log(ev)
    if (ev.key === 'user') {
      const newVal = JSON.parse(ev.newValue || '{}')
      if (newVal.id !== 0 && ev.url.includes('/login')) {
        window.location.reload()
      } else if (!newVal.access_token) {
        logout()
      }
    }
  }

  useEffect(() => {
    if (user.id !== 0 && authLogin.isRegisterPath(history.location.pathname)) {
      store.dispatch(logoutNoRedirectStore())
    }
  }, [history.location.pathname])

  useEffect(() => {
    authLogin.redirectFromMail(history.replace, firstLocation, user)
    window.addEventListener('storage', storageListener, false)

    return () => {
      window.removeEventListener('storage', storageListener, false)
    }
  }, [])

  return (
    <AuthContext.Provider value={{ user, role, getUser, login, logout }}>
      <HeaderBar
        isLogin={!!user}
        notificationAmount={1000}
        user={user}
        role={role}
        logout={logout}
      />
      {user.id !== 0 ? <SideBar /> : null}
      <MainLayoutContainer className={!user.id ? 'is-login-layout' : ''}>
        {user.id !== 0 && !authLogin.isRegisterPath(history.location.pathname)
          ? routes.auth(role)
          : routes.public(role)}
      </MainLayoutContainer>
      <UploadProgress />
      <Toast />
    </AuthContext.Provider>
  )
}

export const useAuth = () => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const ctx = useContext(AuthContext)

  if (ctx === undefined) {
    throw 'AuthContext not initial'
  }

  return ctx
}

export default AuthProvider
